/**
 * Progressive image — <picture> with AVIF → WebP → JPG fallback + LQIP
 * blur placeholder while loading.
 *
 * Usage:
 *   <ProgressivePicture
 *     baseUrl="https://images.unsplash.com/photo-123"
 *     alt="Plombier à Nice"
 *     width={800}
 *     height={600}
 *   />
 *
 * Expects baseUrl to accept `?fm=avif` / `fm=webp` / `fm=jpg` query params
 * (Unsplash API pattern). For self-hosted assets, swap `srcFormat` helper
 * to your pipeline.
 *
 * LQIP: tiny 40-wide blurred JPG rendered as background-image under the
 * full-res <img>, revealed by CSS transition when main image loads.
 */

interface ProgressivePictureProps {
  baseUrl: string
  alt: string
  width: number
  height: number
  className?: string
  sizes?: string
  priority?: boolean // skip lazy + use fetchpriority high
}

function withParam(url: string, params: Record<string, string | number>): string {
  const sep = url.includes('?') ? '&' : '?'
  const qs = Object.entries(params)
    .map(([k, v]) => `${k}=${v}`)
    .join('&')
  return `${url}${sep}${qs}`
}

export function ProgressivePicture({
  baseUrl,
  alt,
  width,
  height,
  className = '',
  sizes = '(max-width: 768px) 100vw, 800px',
  priority = false,
}: ProgressivePictureProps) {
  const avif = withParam(baseUrl, { w: width, q: 78, fm: 'avif', auto: 'format' })
  const webp = withParam(baseUrl, { w: width, q: 80, fm: 'webp', auto: 'format' })
  const jpg = withParam(baseUrl, { w: width, q: 82, fm: 'jpg', auto: 'format' })
  // Tiny placeholder — 40px wide, heavy blur compression
  const lqip = withParam(baseUrl, { w: 40, q: 30, blur: 50, fm: 'jpg' })

  return (
    <picture className={`relative block overflow-hidden ${className}`}>
      <source type="image/avif" srcSet={avif} sizes={sizes} />
      <source type="image/webp" srcSet={webp} sizes={sizes} />
      <img
        src={jpg}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
        style={{
          backgroundImage: `url(${lqip})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        className="h-full w-full object-cover"
      />
    </picture>
  )
}
